import type {ResultadoDeCambioDeExportacion} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useCambiarExportacionDeBolsas} from '../api/inventario';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_CAMBIOS_BIG_BAGS} from './ayuda-inventario';
import {NotaDeAyuda} from './nota-de-ayuda';

const ESTADO: Record<ResultadoDeCambioDeExportacion['estadoNuevo'], [string, string]> = {
  available: ['Disponible', 'Available'],
  assigned: ['Asignado', 'Assigned'],
  exported: ['Exportado', 'Exported'],
};

// Cambiar la exportacion de los Big Bags marcados (o quitarsela): se escribe
// el codigo, se revisa que pasaria y se aplica. La API corrige estado,
// contenedor, historial y bitacora.
export function CambioDeExportacion({bolsas, onListo}: {bolsas: string[]; onListo: () => void}) {
  const cambiar = useCambiarExportacionDeBolsas();
  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [revision, setRevision] = useState<ResultadoDeCambioDeExportacion | null>(null);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  const pedido = () => ({codigos: bolsas, exportacion: codigo.trim() || null});
  const fallo = (error: unknown) =>
    setMensaje({
      texto:
        error instanceof ErrorDeApi ? error.message : tr('No se pudo hacer el cambio.', 'Could not make the change.'),
      error: true,
    });

  const revisar = async () => {
    setMensaje(null);
    try {
      setRevision(await cambiar.mutateAsync({...pedido(), modo: 'revisar'}));
    } catch (error) {
      fallo(error);
    }
  };
  const aplicar = async () => {
    try {
      const r = await cambiar.mutateAsync({...pedido(), modo: 'aplicar'});
      if (r.error) return setMensaje({texto: r.error, error: true});
      setMensaje({
        texto: r.exportacion
          ? tr(
              `Listo: ${numero(r.aplicados)} Big Bags pasaron a ${r.exportacion}.`,
              `Done: ${numero(r.aplicados)} Big Bags moved to ${r.exportacion}.`,
            )
          : tr(
              `Listo: ${numero(r.aplicados)} Big Bags quedaron sin exportación (disponibles).`,
              `Done: ${numero(r.aplicados)} Big Bags no longer have an export (available).`,
            ),
        error: false,
      });
      setRevision(null);
      setAbierto(false);
      setCodigo('');
      onListo();
    } catch (error) {
      fallo(error);
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={!bolsas.length}
        onClick={() => {
          setAbierto(v => !v);
          setRevision(null);
          setMensaje(null);
        }}
        title={tr(
          'Cambia la exportación de los Big Bags marcados, o se la quita',
          'Changes the export of the ticked Big Bags, or removes it',
        )}
        className="rounded-md border border-linea bg-white px-2 py-0.5 text-xs hover:bg-papel disabled:opacity-40"
      >
        ⇄ {tr('Cambiar exportación', 'Change export')}
        {bolsas.length ? ` (${numero(bolsas.length)})` : ''}
      </button>
      {mensaje && (
        <span role="status" className={`text-xs ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}>
          {mensaje.texto}
        </span>
      )}
      {abierto && (
        <div
          role="region"
          aria-label={tr('Cambiar exportación', 'Change export')}
          className="basis-full rounded-lg border border-verde/40 bg-white p-3 text-sm"
        >
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={e => {
              e.preventDefault();
              void revisar();
            }}
          >
            <label className="block text-xs text-apagado">
              {tr('Exportación nueva', 'New export')}
              <NotaDeAyuda texto={AYUDA_CAMBIOS_BIG_BAGS.exportacionNueva} dentroDeEtiqueta />
              <input
                value={codigo}
                onChange={e => {
                  setCodigo(e.target.value);
                  setRevision(null);
                }}
                placeholder={tr('Vacío = sin exportación', 'Empty = no export')}
                className="mt-0.5 block w-48 rounded-md border border-linea px-2 py-1 text-sm text-tinta"
              />
            </label>
            <button
              type="submit"
              disabled={cambiar.isPending}
              className="rounded-md border border-linea bg-white px-3 py-1 text-sm hover:bg-papel"
            >
              {tr('Revisar', 'Review')}
            </button>
            <button type="button" onClick={() => setAbierto(false)} className="text-xs text-apagado hover:text-tinta">
              {tr('Cancelar', 'Cancel')}
            </button>
          </form>
          {revision && (
            <div className="mt-2 space-y-1">
              {revision.error ? (
                <p className="text-alerta">{revision.error}</p>
              ) : (
                <p>
                  {revision.cambios.length
                    ? revision.exportacion
                      ? tr(
                          `${numero(revision.cambios.length)} Big Bags pasan a ${revision.exportacion} y quedan como ${ESTADO[revision.estadoNuevo][0]}. Salen de su contenedor anterior.`,
                          `${numero(revision.cambios.length)} Big Bags move to ${revision.exportacion} as ${ESTADO[revision.estadoNuevo][1]}. They leave their previous container.`,
                        )
                      : tr(
                          `${numero(revision.cambios.length)} Big Bags quedan sin exportación (Disponibles) y salen de su contenedor.`,
                          `${numero(revision.cambios.length)} Big Bags lose their export (Available) and leave their container.`,
                        )
                    : tr('No hay nada que cambiar.', 'There is nothing to change.')}
                </p>
              )}
              {revision.cambios.length > 0 && !revision.error && (
                <ul className="max-h-32 overflow-auto text-xs text-apagado">
                  {revision.cambios.slice(0, 50).map(c => (
                    <li key={c.bbNo}>
                      {c.bbNo}: {c.antes ?? tr('sin exportación', 'no export')} →{' '}
                      {revision.exportacion ?? tr('sin exportación', 'no export')}
                      {c.aviso && <span className="ml-1 text-alerta">⚠ {c.aviso}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {revision.sinCambio.length > 0 && (
                <p className="text-xs text-apagado">
                  {tr(
                    `${numero(revision.sinCambio.length)} ya estaban en esa exportación.`,
                    `${numero(revision.sinCambio.length)} were already in that export.`,
                  )}
                </p>
              )}
              {!revision.error && revision.cambios.length > 0 && (
                <button
                  type="button"
                  onClick={() => void aplicar()}
                  disabled={cambiar.isPending}
                  className="rounded-md bg-verde px-3 py-1 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
                >
                  {cambiar.isPending ? tr('Aplicando…', 'Applying…') : tr('Aplicar el cambio', 'Apply the change')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
